import React, { useState } from "react";
import axios from 'axios';
import {
    Box,
    Typography,
    TextField,
    Button,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    FormLabel,
    Paper,
    Container,
    Snackbar,
    Alert,
    Fade,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import FavoriteIcon from "@mui/icons-material/Favorite";
import CelebrationIcon from "@mui/icons-material/Celebration";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

// Styled components for a premium wedding aesthetic
const StyledPaper = styled(Paper)(({ theme }) => ({
    background: "linear-gradient(145deg, #fdfbfb 0%, #ebedee 100%)",
    borderRadius: "24px",
    padding: "48px",
    boxShadow: "0 20px 60px rgba(0, 0, 0, 0.1), 0 8px 24px rgba(0, 0, 0, 0.08)",
    position: "relative",
    overflow: "hidden",
    "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: "4px",
        background: "linear-gradient(90deg, #d4a574, #c9a66b, #d4a574)",
    },
    [theme.breakpoints.down("sm")]: {
        padding: "32px 24px",
        borderRadius: "16px",
    },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
    "& .MuiOutlinedInput-root": {
        borderRadius: "12px",
        backgroundColor: "#ffffff",
        transition: "all 0.3s ease",
        "&:hover": {
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
        },
        "&.Mui-focused": {
            boxShadow: "0 4px 16px rgba(212, 165, 116, 0.25)",
        },
        "& fieldset": {
            borderColor: "#e0d6cc",
            borderWidth: "2px",
        },
        "&:hover fieldset": {
            borderColor: "#d4a574",
        },
        "&.Mui-focused fieldset": {
            borderColor: "#c9a66b",
        },
    },
    "& .MuiInputLabel-root": {
        color: "#8b7355",
        "&.Mui-focused": {
            color: "#c9a66b",
        },
    },
}));

const StyledButton = styled(Button)(({ theme }) => ({
    background: "linear-gradient(135deg, #d4a574 0%, #c9a66b 50%, #b8956a 100%)",
    color: "#ffffff",
    borderRadius: "12px",
    padding: "14px 48px",
    fontSize: "1.1rem",
    fontWeight: 600,
    textTransform: "none",
    letterSpacing: "0.5px",
    boxShadow: "0 8px 24px rgba(212, 165, 116, 0.4)",
    transition: "all 0.3s ease",
    "&:hover": {
        background: "linear-gradient(135deg, #c9a66b 0%, #b8956a 50%, #a78559 100%)",
        boxShadow: "0 12px 32px rgba(212, 165, 116, 0.5)",
        transform: "translateY(-2px)",
    },
    "&:active": {
        transform: "translateY(0)",
    },
    "&:disabled": {
        background: "#e0d6cc",
        color: "#a09080",
    },
}));

const StyledRadio = styled(Radio)(({ theme }) => ({
    color: "#d4a574",
    "&.Mui-checked": {
        color: "#c9a66b",
    },
}));

const WeddingRSVP = () => {
    const [formData, setFormData] = useState({
        name: "",
        attending: "",
        guestCount: "",
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors((prev) => ({
                ...prev,
                [name]: "",
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = "אנא הזן את שמך";
        }

        if (!formData.attending) {
            newErrors.attending = "אנא בחר את הגעתך";
        }

        if (formData.attending === "yes" && !formData.guestCount) {
            newErrors.guestCount = "אנא הזן את מספר האורחים";
        }

        if (
            formData.attending === "yes" &&
            formData.guestCount &&
            (isNaN(formData.guestCount) || parseInt(formData.guestCount) < 1)
        ) {
            newErrors.guestCount = "אנא הזן מספר אורחים תקין";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);

        try {
            await axios.post('/rsvp', {
                name: formData.name,
                attending: formData.attending,
                guestCount: formData.attending === 'yes' ? parseInt(formData.guestCount) : 0,
            });

            setSnackbar({
                open: true,
                message:
                    formData.attending === "yes"
                        ? "תודה רבה! אנחנו מחכים לחגוג איתך! 🎉"
                        : "תודה שעדכנת אותנו. נתגעגע אליך! 💕",
                severity: "success",
            });

            // Reset form
            setFormData({
                name: "",
                attending: "",
                guestCount: "",
            });
        } catch (error) {
            console.error('Error submitting RSVP:', error);
            setSnackbar({
                open: true,
                message: "משהו השתבש. אנא נסה שנית.",
                severity: "error",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
                background: "linear-gradient(180deg, #f8f4f0 0%, #efe9e3 50%, #e8e0d8 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                py: { xs: 4, md: 8 },
                px: 2,
            }}
        >
            <Container maxWidth="sm">
                <Fade in timeout={800}>
                    <StyledPaper elevation={0}>
                        {/* Decorative Header */}
                        <Box sx={{ textAlign: "center", mb: 4 }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    gap: 1,
                                    mb: 2,
                                }}
                            >
                                <FavoriteIcon
                                    sx={{
                                        color: "#d4a574",
                                        fontSize: "1.5rem",
                                        animation: "pulse 2s infinite",
                                        "@keyframes pulse": {
                                            "0%, 100%": { transform: "scale(1)" },
                                            "50%": { transform: "scale(1.1)" },
                                        },
                                    }}
                                />
                                <CelebrationIcon sx={{ color: "#c9a66b", fontSize: "1.5rem" }} />
                                <FavoriteIcon
                                    sx={{
                                        color: "#d4a574",
                                        fontSize: "1.5rem",
                                        animation: "pulse 2s infinite",
                                        animationDelay: "0.5s",
                                        "@keyframes pulse": {
                                            "0%, 100%": { transform: "scale(1)" },
                                            "50%": { transform: "scale(1.1)" },
                                        },
                                    }}
                                />
                            </Box>

                            <Typography
                                variant="overline"
                                sx={{
                                    color: "#8b7355",
                                    letterSpacing: "4px",
                                    fontSize: "0.85rem",
                                    fontWeight: 500,
                                }}
                            >
                                הנכם מוזמנים
                            </Typography>

                            <Typography
                                variant="h3"
                                sx={{
                                    fontFamily: "'Playfair Display', serif",
                                    fontWeight: 600,
                                    color: "#5a4a3a",
                                    mt: 1,
                                    mb: 1,
                                    fontSize: { xs: "2rem", sm: "2.5rem" },
                                }}
                            >
                                לחתונה של דור רצבי ורותם רוזנפלד
                            </Typography>

                            <Typography
                                variant="body1"
                                sx={{
                                    color: "#8b7355",
                                    fontSize: "1rem",
                                    maxWidth: "350px",
                                    mx: "auto",
                                    lineHeight: 1.6,
                                }}
                            >
                                נשמח לחגוג איתכם את היום המיוחד הזה
                            </Typography>

                            {/* Event Details */}
                            <Box
                                sx={{
                                    mt: 3,
                                    p: 2.5,
                                    borderRadius: "16px",
                                    backgroundColor: "rgba(212, 165, 116, 0.08)",
                                    border: "1px solid rgba(212, 165, 116, 0.2)",
                                }}
                            >
                                {/* Date */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: 1,
                                        mb: 1.5,
                                    }}
                                >
                                    <CalendarMonthIcon sx={{ color: "#c9a66b", fontSize: "1.3rem" }} />
                                    <Typography
                                        sx={{
                                            color: "#5a4a3a",
                                            fontWeight: 600,
                                            fontSize: "1.1rem",
                                        }}
                                    >
                                        27/01/2026
                                    </Typography>
                                </Box>

                                {/* Time */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: 1,
                                        mb: 1.5,
                                    }}
                                >
                                    <AccessTimeIcon sx={{ color: "#c9a66b", fontSize: "1.3rem" }} />
                                    <Typography
                                        sx={{
                                            color: "#5a4a3a",
                                            fontWeight: 600,
                                            fontSize: "1.1rem",
                                        }}
                                    >
                                        19:30
                                    </Typography>
                                </Box>

                                {/* Location */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: 1,
                                        mb: 2,
                                    }}
                                >
                                    <LocationOnIcon sx={{ color: "#c9a66b", fontSize: "1.3rem" }} />
                                    <Typography
                                        sx={{
                                            color: "#5a4a3a",
                                            fontWeight: 500,
                                            fontSize: "1rem",
                                        }}
                                    >
                                        אולמי קסאנדו בפתח תקווה
                                    </Typography>
                                </Box>

                                {/* Waze Button */}
                                <Box
                                    component="a"
                                    href="https://waze.com/ul?ll=32.10819602001625,34.887688466266496&navigate=yes"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        direction: "rtl", // <--- שורה קריטית: מכריח כיוון מימין לשמאל
                                        gap: 1,
                                        px: 2.5,
                                        py: 1,
                                        borderRadius: "10px",
                                        backgroundColor: "#33ccff",
                                        color: "#ffffff",
                                        textDecoration: "none",
                                        fontWeight: 600,
                                        fontSize: "0.95rem",
                                        transition: "all 0.3s ease",
                                        boxShadow: "0 4px 12px rgba(51, 204, 255, 0.3)",
                                        "&:hover": {
                                            backgroundColor: "#00b8e6",
                                            boxShadow: "0 6px 16px rgba(51, 204, 255, 0.4)",
                                            transform: "translateY(-2px)",
                                        },
                                    }}
                                >

                                    <Box
                                        component="img"
                                        src="https://www.waze.com/favicon.ico"
                                        alt="Waze"
                                        sx={{ width: 20, height: 20, direction: "rtl" }}
                                    />

                                    נווט עם Waze
                                </Box>
                            </Box>

                        </Box>

                        {/* Form */}
                        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 4 }}>
                            {/* Name Field */}
                            <StyledTextField
                                fullWidth
                                label="שם מלא"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                error={!!errors.name}
                                helperText={errors.name}
                                sx={{ mb: 3 }}
                                placeholder="הזן את שמך"
                            />

                            {/* Attendance Radio */}
                            <FormControl
                                component="fieldset"
                                error={!!errors.attending}
                                sx={{ mb: 3, width: "100%" }}
                            >
                                <FormLabel
                                    component="legend"
                                    sx={{
                                        color: "#5a4a3a",
                                        fontWeight: 500,
                                        mb: 1,
                                        "&.Mui-focused": {
                                            color: "#5a4a3a",
                                        },
                                    }}
                                >
                                    האם תגיעו לאירוע?
                                </FormLabel>
                                <RadioGroup
                                    name="attending"
                                    value={formData.attending}
                                    onChange={handleChange}
                                    sx={{
                                        flexDirection: "row",
                                        gap: 2,
                                    }}
                                >
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            flex: 1,
                                            p: 2,
                                            borderRadius: "12px",
                                            border: `2px solid ${formData.attending === "yes" ? "#c9a66b" : "#e0d6cc"
                                                }`,
                                            backgroundColor:
                                                formData.attending === "yes" ? "#faf7f4" : "#ffffff",
                                            transition: "all 0.3s ease",
                                            cursor: "pointer",
                                            "&:hover": {
                                                borderColor: "#d4a574",
                                            },
                                        }}
                                        onClick={() =>
                                            handleChange({ target: { name: "attending", value: "yes" } })
                                        }
                                    >
                                        <FormControlLabel
                                            value="yes"
                                            control={<StyledRadio />}
                                            label={
                                                <Typography sx={{ fontWeight: 500, color: "#5a4a3a" }}>
                                                    בשמחה רבה! 🎉
                                                </Typography>
                                            }
                                            sx={{ m: 0 }}
                                        />
                                    </Paper>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            flex: 1,
                                            p: 2,
                                            borderRadius: "12px",
                                            border: `2px solid ${formData.attending === "no" ? "#c9a66b" : "#e0d6cc"
                                                }`,
                                            backgroundColor:
                                                formData.attending === "no" ? "#faf7f4" : "#ffffff",
                                            transition: "all 0.3s ease",
                                            cursor: "pointer",
                                            "&:hover": {
                                                borderColor: "#d4a574",
                                            },
                                        }}
                                        onClick={() =>
                                            handleChange({ target: { name: "attending", value: "no" } })
                                        }
                                    >
                                        <FormControlLabel
                                            value="no"
                                            control={<StyledRadio />}
                                            label={
                                                <Typography sx={{ fontWeight: 500, color: "#5a4a3a" }}>
                                                    לצערנו לא נוכל 💔
                                                </Typography>
                                            }
                                            sx={{ m: 0 }}
                                        />
                                    </Paper>
                                </RadioGroup>
                                {errors.attending && (
                                    <Typography
                                        variant="caption"
                                        sx={{ color: "#d32f2f", mt: 1, ml: 1.5 }}
                                    >
                                        {errors.attending}
                                    </Typography>
                                )}
                            </FormControl>

                            {/* Number of Guests - Only show if attending */}
                            <Fade in={formData.attending === "yes"} timeout={400}>
                                <Box sx={{ display: formData.attending === "yes" ? "block" : "none" }}>
                                    <StyledTextField
                                        fullWidth
                                        label="מספר אורחים (כולל עצמך)"
                                        name="guestCount"
                                        type="number"
                                        value={formData.guestCount}
                                        onChange={handleChange}
                                        error={!!errors.guestCount}
                                        helperText={errors.guestCount}
                                        sx={{ mb: 3 }}
                                        inputProps={{ min: 1 }}
                                        placeholder="לדוגמה: 2"
                                    />
                                </Box>
                            </Fade>

                            {/* Submit Button */}
                            <Box sx={{ textAlign: "center", mt: 4 }}>
                                <StyledButton
                                    type="submit"
                                    variant="contained"
                                    size="large"
                                    disabled={isSubmitting}
                                    fullWidth
                                    sx={{ maxWidth: "280px" }}
                                >
                                    {isSubmitting ? "שולח..." : "שליחת אישור הגעה"}
                                </StyledButton>
                            </Box>
                        </Box>

                        {/* Decorative Footer */}
                        <Box
                            sx={{
                                textAlign: "center",
                                mt: 4,
                                pt: 3,
                                borderTop: "1px solid #e0d6cc",
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    color: "#a09080",
                                    fontStyle: "italic",
                                }}
                            >
                                "שתי נשמות, לב אחד" 💕
                            </Typography>
                        </Box>
                    </StyledPaper>
                </Fade>

                {/* Snackbar for feedback */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                >
                    <Alert
                        onClose={handleCloseSnackbar}
                        severity={snackbar.severity}
                        sx={{
                            borderRadius: "12px",
                            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
                        }}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Container>
        </Box>
    );
};

export default WeddingRSVP;
